import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency } from "@/utils/orderUtils";
import type { InviteMonthSummary } from "@/hooks/sms/useInviteMonthSummary";

/**
 * The Order Invite "This month" roll-up (moved here from the Invitation tab):
 * invited / joined % / opted out with a join-rate bar and the month's spend.
 * Counts Order Invite texts only; a cold JOIN text lands in the Subscribed tile.
 */
export function ContactsMonthCard({ month }: { month: InviteMonthSummary }) {
  const show = (n: number) => (month.isLoading ? "…" : month.isError ? "—" : n.toLocaleString());

  return (
    <Card>
      <CardContent className="grid gap-3 p-4 sm:p-5 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center lg:gap-8">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">This month</h3>
          <div className="grid grid-cols-3 gap-2 text-center lg:gap-8 lg:text-left">
            <div>
              <p className="text-2xl font-bold tabular-nums text-foreground">{show(month.invited)}</p>
              <p className="text-xs text-muted-foreground">invited</p>
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums text-status-success">
                {month.isLoading ? "…" : month.isError ? "—" : `${month.joinRate}%`}
              </p>
              <p className="text-xs text-muted-foreground">joined ({show(month.joined)})</p>
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums text-foreground">{show(month.optedOut)}</p>
              <p className="text-xs text-muted-foreground">opted out</p>
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Join rate</span>
            <span className="tabular-nums">{month.joinRate}%</span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <div className="h-full rounded-full bg-status-success" style={{ width: `${Math.min(month.joinRate, 100)}%` }} />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-border pt-2 text-xs text-muted-foreground lg:block lg:border-0 lg:pt-0 lg:text-right">
          <p>
            Spent this month:{" "}
            <span className="font-medium text-foreground">{month.isError ? "—" : formatCurrency(month.spent)}</span>
          </p>
          <p>
            Counts Order Invite texts ·{" "}
            <Link to="/marketing?tab=invitation" className="font-medium text-status-info underline-offset-2 hover:underline">
              See batches on Invitation
            </Link>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
